import {IconButton, type TableColumnProps} from '@wandb/components';
import React from 'react';
// oxlint-disable-next-line wandb/no-deprecated-imports
import TimeAgo from 'react-timeago';

import {type AliasItem} from '../ArtifactAlias';
import {type ArtifactVersionsTableData} from '../ArtifactOverview/hooks';
import {
  OverflowAliases,
  OverflowTags,
} from '../Registries/common/OverflowContainer';
import {formatNumber} from '../util/formatNumber';
import {getArtifactVersionMetricColor} from './artifactVersionMetricColors';

export type ArtifactVersionSelectionTableRow = Omit<
  ArtifactVersionsTableData,
  'aliases'
> & {
  aliases: AliasItem[];
  selected: boolean;
};

export type ArtifactVersionSelectionColumn = TableColumnProps & {
  name: React.ReactNode;
  Cell: (props: {row: ArtifactVersionSelectionTableRow}) => React.ReactNode;
};

export type ArtifactVersionSelectionMetricValues = Record<
  string,
  Record<string, number | null>
>;

type ArtifactVersionSelectionColumnsArgs = {
  allPageRowsSelected: boolean;
  metricValues: ArtifactVersionSelectionMetricValues;
  metricNames: string[];
  pageSelectionDisabled: boolean;
  selectionLimitReached: boolean;
  togglePageSelection: () => void;
  toggleSelection: (membershipId: string) => void;
};

function formatMetricValue(value: unknown) {
  return value == null ? '-' : formatNumber(value);
}

export function getArtifactVersionSelectionColumns({
  allPageRowsSelected,
  metricValues,
  metricNames,
  pageSelectionDisabled,
  selectionLimitReached,
  togglePageSelection,
  toggleSelection,
}: ArtifactVersionSelectionColumnsArgs): ArtifactVersionSelectionColumn[] {
  return [
    {
      id: 'selected',
      name: (
        <IconButton
          aria-label={
            allPageRowsSelected
              ? 'Remove all artifact versions on this page from selection'
              : 'Select all artifact versions on this page'
          }
          icon={
            allPageRowsSelected ? 'visibility-checked' : 'visibility-unchecked'
          }
          isDisabled={pageSelectionDisabled}
          iconColor="moon"
          size="sm"
          variant="ghost"
          onPress={togglePageSelection}
        />
      ),
      width: 48,
      Cell: ({row}) => {
        const canSelect = row.selected || !selectionLimitReached;
        const ariaLabel = row.selected
          ? 'Remove artifact version from selection'
          : 'Select artifact version';

        return (
          <IconButton
            aria-label={ariaLabel}
            icon={row.selected ? 'visibility-checked' : 'visibility-unchecked'}
            isDisabled={!canSelect}
            iconColor="moon"
            size="sm"
            variant="ghost"
            onPress={() => toggleSelection(row.membershipId)}
          />
        );
      },
    },
    {
      id: 'version',
      name: 'Version',
      defaultWidth: '0.7fr',
      minWidth: 96,
      isRowHeader: true,
      Cell: ({row}) => (
        <div className="flex items-center gap-10">
          <span
            className="h-10 w-10 shrink-0 rounded-full"
            style={{
              backgroundColor: getArtifactVersionMetricColor({
                label: `v${row.versionIndex}`,
                selectionIndex: row.versionIndex,
                versionIndex: row.versionIndex,
              }),
            }}
          />
          <span className="font-semibold text-wb-text-link">
            v{row.versionIndex}
          </span>
        </div>
      ),
    },
    {
      id: 'aliases',
      name: 'Aliases',
      defaultWidth: '1fr',
      minWidth: 120,
      Cell: ({row}) => (
        <div className="w-full overflow-hidden">
          <OverflowAliases aliases={row.aliases} />
        </div>
      ),
    },
    {
      id: 'tags',
      name: 'Tags',
      defaultWidth: '1fr',
      minWidth: 120,
      Cell: ({row}) => (
        <div className="w-full overflow-hidden">
          <OverflowTags tags={row.tags} showColor canDelete={false} />
        </div>
      ),
    },
    {
      id: 'linked',
      name: 'Linked',
      defaultWidth: '0.9fr',
      minWidth: 120,
      Cell: ({row}) => {
        const linkedAt = row.linkedAt ?? row.createdAt;
        return (
          <span className="whitespace-nowrap text-wb-text-secondary dark:text-moon-400">
            {linkedAt ? <TimeAgo date={linkedAt} /> : ''}
          </span>
        );
      },
    },
    ...metricNames.map(metricName => ({
      id: `metric:${metricName}`,
      name: metricName,
      defaultWidth: '0.8fr' as const,
      minWidth: 120,
      Cell: ({row}: {row: ArtifactVersionSelectionTableRow}) =>
        formatMetricValue(metricValues[metricName]?.[row.membershipId]),
    })),
  ];
}
